import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowUpRight, Youtube } from "lucide-react";
import { WHATSAPP_URL, OWNER_PHONE_DISPLAY, OWNER_PHONE_TEL, OWNER_EMAIL, SOCIAL_LINKS } from "@/data/content";
import { WhatsAppIcon, InstagramIcon, LinkedInIcon } from "@/components/BrandIcons";

const socialBtnCls =
  "grid h-11 w-11 place-items-center rounded-lg border border-white/20 transition-all duration-300 ease-out hover:scale-[1.06] hover:border-brand-gold hover:text-brand-gold";

export const Footer = () => (
  <footer
    data-testid="site-footer"
    className="relative bg-gradient-to-br from-brand-navy via-[#0b163f] to-[#061031] text-white"
  >
    <div className="mx-auto max-w-[1400px] px-6 py-16 md:px-12 md:py-20">
      <div className="grid gap-12 md:grid-cols-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-lg bg-brand-cta font-display text-lg font-bold text-brand-navy">
              KB
            </span>
            <div>
              <p className="font-display text-lg font-bold">Kuldeep Batra</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-brand-gold">Educator · Mentor · Speaker</p>
            </div>
          </div>
          <h2 className="mt-6 font-display text-3xl font-extrabold leading-tight md:text-4xl">
            Let's build your <span className="text-brand-gold">next breakthrough.</span>
          </h2>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="footer-contact-cta"
              className="inline-flex min-h-[48px] items-center gap-2 rounded-lg bg-brand-cta px-6 py-3 text-sm font-semibold text-brand-navy transition-all duration-300 ease-out hover:scale-[1.02] hover:bg-brand-ctaDark"
            >
              Book a Consultation
              <ArrowUpRight className="h-4 w-4" />
            </Link>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp"
              className="inline-flex min-h-[48px] items-center gap-2 rounded-lg border border-white/25 px-6 py-3 text-sm font-medium text-white transition-all duration-300 ease-out hover:scale-[1.02] hover:border-brand-gold hover:text-brand-gold"
            >
              <WhatsAppIcon className="h-4 w-4" /> WhatsApp
            </a>
          </div>
        </div>

        {/* Explore (20% larger) */}
        <div className="md:col-span-3 md:col-start-7">
          <p className="mb-5 text-[15px] font-semibold uppercase tracking-[0.25em] text-brand-gold">Explore</p>
          <ul className="space-y-3 text-[17px] text-white/90">
            {[
              ["Home", "/"], ["About", "/about"], ["Courses", "/courses"],
              ["Student Success", "/testimonials"], ["Resources", "/resources"],
              ["Media", "/media"], ["Contact", "/contact"],
            ].map(([label, to]) => (
              <li key={to}>
                <Link to={to} className="transition-colors hover:text-brand-gold">{label}</Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact (20% larger) */}
        <div className="md:col-span-3">
          <p className="mb-5 text-[15px] font-semibold uppercase tracking-[0.25em] text-brand-gold">Contact</p>
          <ul className="space-y-4 text-[17px] text-white/90">
            <li className="flex items-center gap-3">
              <Mail className="h-5 w-5 flex-shrink-0 text-brand-gold" aria-hidden="true" />
              <a
                href={`mailto:${OWNER_EMAIL}`}
                data-testid="footer-email-link"
                className="transition-colors hover:text-brand-gold break-all"
              >
                {OWNER_EMAIL}
              </a>
            </li>
            <li className="flex flex-wrap items-center gap-3">
              <Phone className="h-5 w-5 flex-shrink-0 text-brand-gold" aria-hidden="true" />
              <a
                href={`tel:${OWNER_PHONE_TEL}`}
                data-testid="footer-phone-link"
                className="transition-colors hover:text-brand-gold"
              >
                {OWNER_PHONE_DISPLAY}
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Chat on WhatsApp"
                data-testid="footer-phone-whatsapp"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full transition-all duration-300 ease-out hover:scale-[1.08]"
              >
                <WhatsAppIcon className="h-6 w-6" />
              </a>
            </li>
            <li className="flex items-center gap-3">
              <MapPin className="h-5 w-5 flex-shrink-0 text-brand-gold" aria-hidden="true" />
              <span>Available across India</span>
            </li>
          </ul>

          <div className="mt-6 flex gap-3">
            <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className={socialBtnCls}>
              <LinkedInIcon className="h-4 w-4" />
            </a>
            <a href={SOCIAL_LINKS.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className={socialBtnCls}>
              <Youtube className="h-4 w-4" aria-hidden="true" />
            </a>
            <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className={socialBtnCls}>
              <InstagramIcon className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>

      <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 text-xs text-white/60 md:flex-row md:items-center">
        <p>© {new Date().getFullYear()} Kuldeep Batra. All rights reserved.</p>
        <p>Educator · Corporate Trainer · AI &amp; Finance · Leadership</p>
      </div>
    </div>
  </footer>
);
